import { PageTranslationProvider } from 'src/lib/translation/PageTranslationProvider';
import { styled } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Head } from '@unhead/react';
import { UnheadProvider, createHead } from '@unhead/react/client';
import React, { Suspense, useEffect, useLayoutEffect, useMemo } from 'react';
import { NetworkStatus, createNetworkStatusNotifier } from 'react-apollo-network-status';
import { useTranslation } from 'react-i18next';
import { Navigate, Routes as ReactRouterRoutes, Route, useLocation, useMatch } from 'react-router';
import { Provider as APIProvider } from 'src/api/provider';
import {
    DEFAULT_SEGMENT_WRITE_KEY,
    MAINTENANCE_MODE,
    OPS_SEGMENT_WRITE_KEY,
    ORC_SEGMENT_WRITE_KEY,
    PMC_SEGMENT_WRITE_KEY,
    RESQ_LITE_BASE_PATH,
    RMC_SEGMENT_WRITE_KEY,
} from 'src/constants/environment';
import { FONT_URL } from 'src/constants/ui';
import { AccountType } from 'src/graphql/lib/server-types';
import type { SessionDetailsFragment } from 'src/graphql/common/fragments/SessionDetails.generated';
import { GraphqlProvider } from 'src/helpers/graphql';
import { AnalyticsProvider } from 'src/lib/analytics';
import { AnalyticsContextProvider } from 'src/lib/analytics/context';
import {
    AnalyticsAccountType,
    AnalyticsEventSource,
    AnalyticsProductWorkflow,
    AnalyticsSource,
} from 'src/lib/interactionTypes';
import { ThemeProvider } from 'src/lib/style';
import { handleAuthenticatedExternalAuthRedirect } from 'src/routes/Auth/externalAuthHelpers';
import { baseTheme } from 'src/theme';
import { AuthThemeProvider } from './Auth/AuthThemeProvider';
import { GoogleLogin } from './Auth/GoogleLogin';
import { MagicLink } from './Auth/MagicLink';
import { MobileAuthCallbackPage } from './Auth/MobileAuthCallbackPage';
import SingleSignOn from './Auth/SingleSignOn';
import ForgotPassword from './ForgotPassword';
import Impersonate from './Impersonate';
import { MaintenancePage } from './MaintenancePage';
import QRCodeLanding from './QRCodeLanding';
import { ResQRouter } from './ResQRouter';
import ResetPassword from './ResetPassword';
import { SignOut } from './SignOut';
import VendorLinkInvalid from './VendorLinkInvalid';
import { BannerProvider } from './bannerContext';
import { AppLoadingSurface } from './components/AppLoadingSurface';
import Loader from './components/atoms/Loader';
import SentryUserContext from './components/domain/SentryUserContext';
import { ConfirmLeaveProvider } from './confirmLeaveContext';
import {
    ClientFeatureFlagsBootstrapProvider,
    useClientFeatureFlagsBootstrap,
} from './clientFeatureFlagsBootstrapContext';
import { FeatureFlagsProvider } from './featureFlagsContext';
import { lazyComponentLoader, lazyNamedComponentLoader } from './helpers';
import {
    LoadingAnimationPreferenceProvider,
    useLoadingAnimationPreference,
} from './loadingAnimationPreferenceContext';
import { LocaleProvider } from './localeContext';
import { NavBarProvider } from './navBarContext';
import { SessionProvider, useNullableSessionUser } from './sessionContext';
import { SnackbarProvider } from './snackbar';
import { bootstrapWindowImpersonation } from './impersonationBootstrap';

const CoordinatorDashboard = lazyComponentLoader(() => import('./CoordinatorDashboard'));
const ClientDashboard = lazyComponentLoader(() => import('./ClientDashboard'));
const VendorDashboard = lazyComponentLoader(() => import('./VendorDashboard'));
const MissingOrganizationPage = lazyComponentLoader(() => import('./MissingOrganizationPage'));
const MCPAuthorization = lazyComponentLoader(() => import('./MCPAuthorization/MCPAuthorization'));
const Auth = lazyComponentLoader(() => import('./Auth'));
const OffResqCustomerPortal = lazyComponentLoader(() => import('./OffResqCustomer'));
const PublicBusinessProfile = lazyComponentLoader(
    () => import('../lib/vendor/PublicBusinessProfile'),
);
const PublicVisitSignature = lazyNamedComponentLoader(
    () => import('./PublicVisitSignature'),
    'PublicVisitSignature',
);

bootstrapWindowImpersonation();

interface PlatformResult {
    platform: React.LazyExoticComponent<React.ComponentType<any>> | null;
    analyticsSource: AnalyticsSource;
    productWorkflow: AnalyticsProductWorkflow | null;
    writeKey: string;
    accountType: AnalyticsAccountType | undefined;
}

const getPlatformTranslationNamespaces = (
    accountType: AnalyticsAccountType | undefined,
): string[] => {
    switch (accountType) {
        case AnalyticsAccountType.RCC:
            return ['ops.workOrders', 'lib.common'];
        case AnalyticsAccountType.RMC:
            return ['client.dashboard', 'lib.common'];
        case AnalyticsAccountType.PMC:
            return ['vendor.dashboard', 'lib.common'];
        case AnalyticsAccountType.OFF_RESQ_CUSTOMER_PORTAL:
            return ['offResqCustomer.index', 'lib.common'];
        default:
            return ['auth.login', 'lib.common'];
    }
};

const AUTH_TRANSLATION_NAMESPACES = ['auth.login', 'lib.common'];

const TranslationReady = ({
    namespaces,
    children,
}: {
    namespaces: string[];
    children: React.ReactNode;
}) => {
    const { ready } = useTranslation(namespaces);
    return ready ? children : <AppLoadingSurface />;
};

const usePlatform = (me: SessionDetailsFragment | null): PlatformResult => {
    const resqLiteMatch = useMatch(`${RESQ_LITE_BASE_PATH}/*`);
    const legacyResqLiteMatch = useMatch('/fast-pay-customer/*');
    const offResQCustomerMatch = resqLiteMatch ?? legacyResqLiteMatch;
    const acceptInviteMatch = useMatch('/accept-invite/*');
    const declineInviteMatch = useMatch('/decline-invite/*');
    const inviteAlreadyAcceptedMatch = useMatch('/invite-already-accepted/*');
    const exceptionRouteMatch =
        acceptInviteMatch || declineInviteMatch || inviteAlreadyAcceptedMatch;

    // Handle external auth flows (mobile, CLI) for authenticated users.
    // useLayoutEffect prevents a paint of dashboard UI before redirecting back to the external client.
    useLayoutEffect(() => {
        if (!me) return;

        // Check sessionStorage for external auth callbacks and redirect if found
        // This handles: OAuth completing, or user landing on /login while already authenticated
        handleAuthenticatedExternalAuthRedirect();
    }, [me]);

    // useMemo required - useMatch returns new object references on every render.
    // Without memoization, AnalyticsContextProvider and AnalyticsProvider receive new
    // prop objects every render, causing cascading re-renders and potential state loss
    // that breaks navigation after the first click.
    return useMemo((): PlatformResult => {
        if (offResQCustomerMatch) {
            return {
                platform: OffResqCustomerPortal,
                analyticsSource: AnalyticsSource.OFF_RESQ_CUSTOMER_PORTAL,
                productWorkflow: null,
                writeKey: ORC_SEGMENT_WRITE_KEY,
                accountType: AnalyticsAccountType.OFF_RESQ_CUSTOMER_PORTAL,
            };
        }

        if (me && !exceptionRouteMatch) {
            if ([AccountType.Coordinator, AccountType.ControlCenter].includes(me.accountType)) {
                return {
                    platform: CoordinatorDashboard,
                    analyticsSource: AnalyticsSource.RCC,
                    productWorkflow: null,
                    writeKey: OPS_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.RCC,
                };
            }

            if (me.accountType === AccountType.Client) {
                return {
                    platform: ClientDashboard,
                    analyticsSource: AnalyticsSource.RMC,
                    productWorkflow: null,
                    writeKey: RMC_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.RMC,
                };
            }

            if (me.accountType === AccountType.Vendor) {
                return {
                    platform: VendorDashboard,
                    analyticsSource: AnalyticsSource.PMC,
                    productWorkflow: null,
                    writeKey: PMC_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.PMC,
                };
            }

            if (me.accountType === AccountType.OffPlatform) {
                return {
                    platform: OffResqCustomerPortal,
                    analyticsSource: AnalyticsSource.OFF_RESQ_CUSTOMER_PORTAL,
                    productWorkflow: null,
                    writeKey: PMC_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.OFF_RESQ_CUSTOMER_PORTAL,
                };
            }

            if (me.accountType === AccountType.Independent) {
                return {
                    platform: MissingOrganizationPage,
                    analyticsSource: AnalyticsSource.INDEPENDENT,
                    productWorkflow: null,
                    writeKey: PMC_SEGMENT_WRITE_KEY,
                    accountType: AnalyticsAccountType.INDEPENDENT,
                };
            }
        }

        return {
            platform: Auth,
            analyticsSource: AnalyticsSource.LOGIN,
            productWorkflow: AnalyticsProductWorkflow.Login,
            writeKey: DEFAULT_SEGMENT_WRITE_KEY,
            accountType: undefined,
        };
    }, [me, offResQCustomerMatch, exceptionRouteMatch]);
};

const Routes = React.memo(() => {
    const me = useNullableSessionUser();
    const location = useLocation();

    const { platform, analyticsSource, productWorkflow, writeKey, accountType } = usePlatform(me);
    const { standardClientOrgCode, clearStandardClient } = useClientFeatureFlagsBootstrap();
    const currentOrgCode = location.pathname.split('/').filter(Boolean)[0] ?? null;
    const isClientPortal = accountType === AnalyticsAccountType.RMC;
    const standardClientIsConfirmed =
        isClientPortal &&
        standardClientOrgCode !== null &&
        standardClientOrgCode === currentOrgCode;

    useEffect(() => {
        if (
            standardClientOrgCode &&
            (!isClientPortal || standardClientOrgCode !== currentOrgCode)
        ) {
            clearStandardClient();
        }
    }, [clearStandardClient, currentOrgCode, isClientPortal, standardClientOrgCode]);

    // useMemo required - context value passed to AnalyticsContextProvider.
    // Without stable reference, context consumers re-render on every Routes render.
    const analyticsContextValue = useMemo(
        () => ({
            application: analyticsSource,
            eventSource: AnalyticsEventSource.FRONTEND,
            productWorkflow,
        }),
        [analyticsSource, productWorkflow],
    );
    const platformTranslationNamespaces = useMemo(
        () => getPlatformTranslationNamespaces(accountType),
        [accountType],
    );

    return (
        <AnalyticsContextProvider value={analyticsContextValue}>
            <AnalyticsProvider
                segmentWriteKey={writeKey}
                accountType={accountType}
                skipFeatureFlags={isClientPortal && !standardClientIsConfirmed}
            >
                <ReactRouterRoutes>
                    <Route path="/sign-out" element={<SignOut />} />
                    {['/mobile-auth/facility/callback', '/mobile-auth/vendor/callback'].map(
                        (path) => (
                            <Route
                                key={path}
                                path={path}
                                element={
                                    <AuthThemeProvider>
                                        <MobileAuthCallbackPage />
                                    </AuthThemeProvider>
                                }
                            />
                        ),
                    )}
                    <Route path="/impersonate/:userId" element={<Impersonate />} />
                    <Route path="/qrcode" element={<QRCodeLanding />} />
                    <Route path="/invalid-link" element={<VendorLinkInvalid />} />
                    <Route
                        path="/reset-password/:userId/:token"
                        element={
                            <TranslationReady namespaces={AUTH_TRANSLATION_NAMESPACES}>
                                <AuthThemeProvider>
                                    <ResetPassword />
                                </AuthThemeProvider>
                            </TranslationReady>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <TranslationReady namespaces={AUTH_TRANSLATION_NAMESPACES}>
                                <AuthThemeProvider>
                                    <ForgotPassword />
                                </AuthThemeProvider>
                            </TranslationReady>
                        }
                    />
                    <Route
                        path="/password-free-link"
                        element={
                            <TranslationReady namespaces={AUTH_TRANSLATION_NAMESPACES}>
                                <AuthThemeProvider>
                                    <MagicLink />
                                </AuthThemeProvider>
                            </TranslationReady>
                        }
                    />
                    <Route
                        path="/sso"
                        element={
                            <TranslationReady namespaces={AUTH_TRANSLATION_NAMESPACES}>
                                <AuthThemeProvider>
                                    <SingleSignOn />
                                </AuthThemeProvider>
                            </TranslationReady>
                        }
                    />
                    <Route
                        path="/auth/google"
                        element={
                            <TranslationReady namespaces={AUTH_TRANSLATION_NAMESPACES}>
                                <AuthThemeProvider>
                                    <GoogleLogin />
                                </AuthThemeProvider>
                            </TranslationReady>
                        }
                    />
                    <Route
                        path="/mcp/authorize"
                        element={
                            <TranslationReady namespaces={AUTH_TRANSLATION_NAMESPACES}>
                                <AuthThemeProvider>
                                    <MCPAuthorization />
                                </AuthThemeProvider>
                            </TranslationReady>
                        }
                    />
                    <Route
                        path="/public/visit-signature/:token"
                        element={<PublicVisitSignature />}
                    />
                    <Route path="/business/:id" element={<PublicBusinessProfile />} />
                    {platform ? (
                        <Route
                            path="*"
                            element={
                                <TranslationReady namespaces={platformTranslationNamespaces}>
                                    {React.createElement(platform)}
                                </TranslationReady>
                            }
                        />
                    ) : (
                        <Route path="*" element={<Navigate to="/sign-out" replace />} />
                    )}
                </ReactRouterRoutes>
            </AnalyticsProvider>
        </AnalyticsContextProvider>
    );
});

const Wrapper = styled('div')``;

type Wrap = (children?: React.ReactNode) => React.ReactElement<any, any> | null;

const wrap = (layers: Wrap[]) => {
    return layers
        .reverse()
        .reduce(
            (children, layerWrap) => layerWrap(children),
            null as React.ReactElement<any, any> | null,
        );
};

const LoaderComponent = ({ apolloNetworkStatus }: { apolloNetworkStatus: NetworkStatus }) => {
    const { reduceLoadingMotion } = useLoadingAnimationPreference();

    return apolloNetworkStatus.numPendingQueries > 0 && !reduceLoadingMotion ? <Loader /> : null;
};

const RuntimeApp = () => {
    // useMemo required - createNetworkStatusNotifier() creates stateful Apollo link.
    // Without memoization, new link created every render → new Apollo client → infinite loop.
    const { link: networkStatusNotifierLink, useApolloNetworkStatus } = useMemo(
        () => createNetworkStatusNotifier(),
        [],
    );

    const apolloNetworkStatus = useApolloNetworkStatus();

    // useMemo required - createHead() creates singleton document head manager.
    // Recreating causes repeated DOM mutations and potential memory leaks.
    const head = useMemo(
        () =>
            createHead({
                // Disable DOM updates in test environment to prevent conflicts
                document:
                    process.env.NODE_ENV === 'test' || process.env.TESTING === 'true'
                        ? undefined
                        : typeof window !== 'undefined'
                          ? window.document
                          : undefined,
            }),
        [],
    );

    // useMemo required - wrap() builds the entire provider tree structure.
    // Without memoization, all providers remount every render → state loss and infinite loops.
    const Wrapped = useMemo(
        () =>
            wrap([
                (children) => <ResQRouter>{children}</ResQRouter>,
                (children) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        {children}
                    </LocalizationProvider>
                ),
                (children) => <ThemeProvider theme={baseTheme}>{children}</ThemeProvider>,
                (children) => <SnackbarProvider>{children}</SnackbarProvider>,
                (children) => <APIProvider>{children}</APIProvider>,
                (children) => (
                    <GraphqlProvider networkStatusNotifierLink={networkStatusNotifierLink}>
                        {children}
                    </GraphqlProvider>
                ),
                (children) => <SessionProvider>{children}</SessionProvider>,
                (children) => <FeatureFlagsProvider>{children}</FeatureFlagsProvider>,
                (children) => <LocaleProvider>{children}</LocaleProvider>,
                (children) => <PageTranslationProvider>{children}</PageTranslationProvider>,
                (children) => <BannerProvider>{children}</BannerProvider>,
                (children) => <NavBarProvider>{children}</NavBarProvider>,
                (children) => (
                    <ClientFeatureFlagsBootstrapProvider>
                        {children}
                    </ClientFeatureFlagsBootstrapProvider>
                ),
                () => (
                    <ConfirmLeaveProvider>
                        <SentryUserContext />
                        <Suspense fallback={<AppLoadingSurface />}>
                            <Wrapper>
                                <Routes />
                            </Wrapper>
                        </Suspense>
                    </ConfirmLeaveProvider>
                ),
            ]),
        [networkStatusNotifierLink],
    );

    return (
        <UnheadProvider head={head}>
            <LoadingAnimationPreferenceProvider>
                <Head>
                    <link rel="stylesheet" href={FONT_URL} />
                </Head>
                <LoaderComponent apolloNetworkStatus={apolloNetworkStatus} />
                {Wrapped}
            </LoadingAnimationPreferenceProvider>
        </UnheadProvider>
    );
};

export const App = () => {
    if (MAINTENANCE_MODE) {
        return <MaintenancePage />;
    }

    return <RuntimeApp />;
};
